import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Activity,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  Columns3,
  GitBranch,
  LayoutDashboard,
  Pencil,
  Play,
  Save,
  Search,
  Table2,
  X,
} from 'lucide-react';
import { useToast } from '../../../../components/common/Toast';
import { applyColumnClassifications, classificationKey, useColumnClassifications } from '../columnClassificationStore';
import { HIDDEN_TABLE_DETAIL_TAB_IDS, SOURCE_META, TABLE_DETAIL_TABS } from '../dataModelsConstants';
import { logDataModels } from '../dataModelsLogger';
import { buildTableDetail } from '../dataModelsTableDetail';
import type { DataModel, DataSource, ModelTable, Space, TableColumn, TableDetailTabId } from '../dataModelsTypes';
import { formatRelative, formatRowCount } from '../dataModelsUtils';
import { assetTypeLabel, isFileAsset } from '../fileAssets';
import { fetchFilePreview, formatFileSize, type FilePreviewResponse } from '../filePreviewApi';
import { CLASSIFICATIONS, type Classification } from '../governance/types';
import {
  buildAssetSelectSql,
  DEFAULT_PREVIEW_PAGE_SIZE,
  fetchTablePreview,
  formatPreviewCell,
  PREVIEW_PAGE_SIZES,
  runAssetQuery,
  searchTablePreview,
  type TablePreviewResponse,
} from '../tablePreviewApi';
import QueryBuilderModal from '../../configurations/ingestion/popups/QueryBuilderModal';
import CatalogGovernancePanel from './CatalogGovernancePanel';
import { getConnectorDisplayName } from '../../Connections/catalog';
import DataModelsAuditPanel from './DataModelsAuditPanel';
import QueryLogsPanel from './QueryLogsPanel';
import FilePreview, { PreviewExpandButton, previewShellClass } from './FilePreview';
import { SourceIconTile, StatusPill, ghostBtn, searchClass, surfaces, tagTone } from './dataModelsUi';

type LiveLoadState = 'loading' | 'ready' | 'error' | 'missing';

export default function TableDetail({
  isLight,
  space,
  model,
  table,
  source,
  onBack,
  tab,
  onTab,
}: {
  isLight: boolean;
  space: Space;
  model: DataModel;
  table: ModelTable;
  source?: DataSource;
  onBack: () => void;
  tab: TableDetailTabId;
  onTab: (tab: TableDetailTabId) => void;
}) {
  const isFile = isFileAsset(table, source);
  const visibleTabs = TABLE_DETAIL_TABS.filter((item) => {
    if (HIDDEN_TABLE_DETAIL_TAB_IDS.has(item.id)) return false;
    if (isFile && item.id === 'schema') return false;
    return true;
  });
  const [schemaQuery, setSchemaQuery] = useState('');
  const [draft, setDraft] = useState<Record<string, Classification | null>>({});
  const [livePreview, setLivePreview] = useState<TablePreviewResponse | null>(null);
  const [liveStatus, setLiveStatus] = useState<LiveLoadState>('loading');
  const [liveError, setLiveError] = useState('');
  const [filePreview, setFilePreview] = useState<FilePreviewResponse | null>(null);
  const [fileStatus, setFileStatus] = useState<LiveLoadState>('loading');
  const [fileError, setFileError] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PREVIEW_PAGE_SIZE);
  const [searchApplied, setSearchApplied] = useState('');
  const [paging, setPaging] = useState(false);
  const [queryOpen, setQueryOpen] = useState(false);
  const previewReadyRef = useRef(false);
  const previewColumnsRef = useRef<string[]>([]);
  const { overrides, saveOverrides } = useColumnClassifications();
  const { showToast } = useToast();
  const detail = useMemo(() => buildTableDetail(table, model, source, overrides), [model, overrides, source, table]);
  const previewOverrides = useMemo(() => {
    const next = { ...overrides };
    Object.entries(draft).forEach(([columnName, value]) => {
      next[classificationKey(table.id, columnName)] = value;
    });
    return next;
  }, [draft, overrides, table.id]);
  const liveColumns = useMemo(() => {
    if (!livePreview) return [] as TableColumn[];
    return applyColumnClassifications(
      livePreview.columns.map((column) => ({
        name: column.name,
        type: column.type,
        nullable: true,
        key: '' as const,
        pii: false,
        description: '',
        tags: [],
      })),
      table.id,
      previewOverrides,
    );
  }, [livePreview, previewOverrides, table.id]);
  const ui = surfaces(isLight);
  const muted = ui.muted;
  const border = ui.border;
  const card = ui.card;
  const schemaRows = liveColumns.filter((column) => {
    const q = schemaQuery.trim().toLowerCase();
    if (!q) return true;
    return `${column.name} ${column.type} ${column.description} ${column.classification ?? ''} ${column.tags.join(' ')}`.toLowerCase().includes(q);
  });
  const dirty = Object.keys(draft).length > 0;

  useEffect(() => {
    setDraft({});
    setPage(1);
    setSearchApplied('');
    previewReadyRef.current = false;
    previewColumnsRef.current = [];
    setFilePreview(null);
    setFileError('');
  }, [table.id]);

  useEffect(() => {
    if (HIDDEN_TABLE_DETAIL_TAB_IDS.has(tab)) onTab('overview');
    else if (isFile && tab === 'schema') onTab('preview');
  }, [isFile, onTab, tab]);

  useEffect(() => {
    if (!isFile) {
      setFilePreview(null);
      setFileStatus('ready');
      setFileError('');
      return undefined;
    }
    let cancelled = false;
    const sourceId = (table.sourceId || '').trim();
    if (!sourceId) {
      setFilePreview(null);
      setFileStatus('missing');
      setFileError('This file has no source connection, so preview is unavailable.');
      logDataModels('warning', 'file_preview_missing_source', { tableId: table.id, tableName: table.name });
      return undefined;
    }
    if (!(table.path || '').trim()) {
      setFilePreview(null);
      setFileStatus('missing');
      setFileError('This file cannot be opened until it is added again from Connections.');
      logDataModels('warning', 'file_preview_missing_path', { tableId: table.id, tableName: table.name });
      return undefined;
    }
    setFileStatus('loading');
    setFileError('');
    fetchFilePreview({ sourceId, tableName: table.name, tableId: table.id, path: table.path })
      .then((data) => {
        if (cancelled) return;
        setFilePreview(data);
        setFileStatus('ready');
      })
      .catch((err) => {
        if (cancelled) return;
        setFilePreview(null);
        setFileStatus('error');
        setFileError(err instanceof Error ? err.message : 'Unable to load file preview');
      });
    return () => {
      cancelled = true;
    };
  }, [isFile, table.id, table.sourceId, table.name, table.path]);

  useEffect(() => {
    if (isFile) return undefined;
    let cancelled = false;
    const sourceId = (table.sourceId || '').trim();
    if (!sourceId) {
      setLivePreview(null);
      setLiveStatus('missing');
      setLiveError('This table has no source connection, so live schema and preview are unavailable.');
      logDataModels('warning', 'table_preview_missing_source', { tableId: table.id, tableName: table.name });
      return undefined;
    }
    const firstLoad = !previewReadyRef.current;
    if (firstLoad) setLiveStatus('loading');
    else setPaging(true);
    setLiveError('');
    const request = searchApplied
      ? searchTablePreview({
          sourceId,
          tableName: table.name,
          path: table.path,
          columns: previewColumnsRef.current,
          search: searchApplied,
          page,
          pageSize,
        })
      : fetchTablePreview({
          sourceId,
          tableName: table.name,
          path: table.path,
          page,
          pageSize,
        });
    request
      .then((data) => {
        if (cancelled) return;
        previewReadyRef.current = true;
        if (data.columns.length) {
          previewColumnsRef.current = data.columns.map((column) => column.name).filter(Boolean);
        }
        setLivePreview(data);
        setLiveStatus('ready');
        if (data.page !== page) setPage(data.page);
      })
      .catch((err) => {
        if (cancelled) return;
        if (firstLoad) {
          setLivePreview(null);
          setLiveStatus('error');
        }
        setLiveError(err instanceof Error ? err.message : 'Unable to load live table preview');
      })
      .finally(() => {
        if (!cancelled) setPaging(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isFile, table.id, table.sourceId, table.name, table.path, page, pageSize, searchApplied]);

  const savedClass = (columnName: string): Classification | null => {
    const column = liveColumns.find((item) => item.name === columnName);
    return CLASSIFICATIONS.includes(column?.classification as Classification) ? (column?.classification as Classification) : null;
  };

  const onClassify = (columnName: string, value: Classification | null) => {
    setDraft((current) => {
      const next = { ...current };
      if (value === savedClass(columnName)) delete next[columnName];
      else next[columnName] = value;
      return next;
    });
  };

  const onSaveClassifications = () => {
    if (!dirty) return;
    const patch = Object.fromEntries(
      Object.entries(draft).map(([columnName, value]) => [classificationKey(table.id, columnName), value]),
    );
    saveOverrides(patch);
    setDraft({});
    showToast({ type: 'success', message: 'Classifications saved' });
  };

  const openQueryBuilder = () => {
    if (!(table.sourceId || '').trim()) {
      showToast({ type: 'error', message: 'This table has no source connection, so Query Data is unavailable.' });
      return;
    }
    setQueryOpen(true);
  };

  return (
    <section className={`flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden ${ui.body}`}>
      <div className={`shrink-0 border-b px-5 py-3 ${border} ${ui.tabBar}`}>
        <div className={`mb-2 flex flex-wrap items-center gap-1 text-xs ${muted}`}>
          <button type="button" onClick={onBack} className="hover:underline">WorkSpace</button>
          <ChevronRight size={12} />
          <button type="button" onClick={onBack} className="hover:underline">{space.name}</button>
          <ChevronRight size={12} />
          <span>{model.name}</span>
          <ChevronRight size={12} />
          <span className={isLight ? 'text-[#1C1C1C]' : 'text-white'}>{table.name}</span>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2">
            <span className={`inline-flex h-7 w-7 items-center justify-center rounded-md ${isLight ? 'bg-[#F2F4F7] text-[#4B5563]' : 'bg-white/10 text-slate-300'}`}>
              <Table2 size={15} />
            </span>
            <h2 className={`truncate font-mono text-base font-semibold ${isLight ? 'text-[#1C1C1C]' : 'text-white'}`}>{table.name}</h2>
            <StatusPill status={table.status} isLight={isLight} />
          </div>
          {isFile ? null : (
            <button type="button" className={ghostBtn(isLight)} onClick={openQueryBuilder}>
              <Play size={14} />
              Open in Query
            </button>
          )}
        </div>
      </div>

      <div className={`flex shrink-0 gap-1 overflow-x-auto border-b px-3 ${border} ${ui.tabBar}`}>
        {visibleTabs.map((item) => {
          const active = tab === item.id;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => onTab(item.id)}
              className={`relative shrink-0 px-3 py-2 text-[13px] ${active ? `font-semibold ${ui.accentText}` : muted}`}
            >
              {item.label}
              {active ? <span className={`absolute inset-x-2 bottom-0 h-0.5 ${ui.accent}`} /> : null}
            </button>
          );
        })}
      </div>

      <div className="relative min-h-0 flex-1">
        <div className="absolute inset-0 overflow-y-auto overflow-x-hidden px-5 py-4">
          {tab === 'overview' ? (
            <OverviewBody
              isLight={isLight}
              table={table}
              source={source}
              modelName={model.name}
              isFile={isFile}
              filePreview={filePreview}
              schemaQuery={schemaQuery}
              onSchemaQuery={setSchemaQuery}
              schemaRows={schemaRows}
              columnCount={liveColumns.length}
              liveStatus={liveStatus}
              liveError={liveError}
              owner={model.owner}
              onShowAll={() => onTab('schema')}
              tags={model.tags}
            />
          ) : null}
          {tab === 'schema' && !isFile ? (
            <SchemaCard
              isLight={isLight}
              total={liveColumns.length}
              rows={schemaRows}
              query={schemaQuery}
              onQuery={setSchemaQuery}
              onClassify={onClassify}
              onSave={onSaveClassifications}
              dirty={dirty}
              liveStatus={liveStatus}
              liveError={liveError}
              showAll
            />
          ) : null}
          {tab === 'preview' && isFile ? (
            fileStatus === 'ready' && filePreview ? (
              <FilePreview isLight={isLight} file={filePreview} />
            ) : (
              <article className={`rounded-2xl border p-4 ${card}`}>
                <h3 className={`mb-2 inline-flex items-center gap-2 text-sm font-semibold ${isLight ? 'text-neutral-900' : 'text-white'}`}>
                  <LayoutDashboard size={15} />
                  Data Preview
                </h3>
                <p className={`px-1 py-8 text-center text-sm ${muted}`}>
                  {fileStatus === 'loading' ? 'Loading file…' : fileError || 'Unable to load file preview.'}
                </p>
              </article>
            )
          ) : null}
          {tab === 'preview' && !isFile ? (
            <PreviewCard
              isLight={isLight}
              preview={livePreview}
              liveStatus={liveStatus}
              liveError={liveError}
              paging={paging}
              page={page}
              pageSize={pageSize}
              onPage={setPage}
              onPageSize={(size) => {
                setPageSize(size);
                setPage(1);
              }}
              searchApplied={searchApplied}
              onSearch={(term) => {
                if (term === searchApplied) return;
                logDataModels('app', 'table_preview_search', { tableId: table.id, hasSearch: Boolean(term), searchLen: term.length });
                setSearchApplied(term);
                setPage(1);
              }}
              onQuery={openQueryBuilder}
              full
            />
          ) : null}
          {tab === 'governance' ? <CatalogGovernancePanel isLight={isLight} level="asset" space={space} model={model} table={table} /> : null}
          {tab === 'relationships' ? <RelationshipsCard isLight={isLight} relations={detail.relationships} /> : null}
          {tab === 'usage' ? <UsageCard isLight={isLight} usage={detail.usage} /> : null}
          {tab === 'lineage' ? <LineageCard isLight={isLight} table={table} source={source} relations={detail.relationships} /> : null}
          {tab === 'docs' ? (
            <article className={`rounded-2xl border p-4 ${card}`}>
              <h3 className={`mb-2 text-sm font-semibold ${isLight ? 'text-neutral-900' : 'text-white'}`}>Documentation</h3>
              <p className={`text-sm leading-6 ${muted}`}>{detail.docs}</p>
            </article>
          ) : null}
          {tab === 'activity' ? (
            <DataModelsAuditPanel
              isLight={isLight}
              workspaceId={space.id}
              catalogId={model.id}
              assetId={table.id}
              streams={['asset', 'policy']}
            />
          ) : null}
          {tab === 'queries' ? (
            <QueryLogsPanel isLight={isLight} workspaceId={space.id} catalogId={model.id} assetId={table.id} />
          ) : null}
        </div>
      </div>
      {queryOpen && !isFile ? (
        <QueryBuilderModal
          onClose={() => setQueryOpen(false)}
          datastoreId={table.sourceId}
          connectionName={`${source?.name || 'Connection'} · ${table.name}`}
          initialQuery={buildAssetSelectSql(table.name, table.path)}
          hideSubmit
          executeQuery={(sql) => runAssetQuery({ sourceId: table.sourceId, tableName: table.name, path: table.path, query: sql })}
          onSubmitTemplate={() => setQueryOpen(false)}
        />
      ) : null}
    </section>
  );
}

function liveNotice(status: LiveLoadState, error: string, emptyLabel: string): string | null {
  if (status === 'loading') return 'Loading live schema…';
  if (status === 'missing') return error || 'This table has no source connection, so live schema and preview are unavailable.';
  if (status === 'error') return error || 'Unable to load live table preview.';
  return emptyLabel;
}

function OverviewBody({
  isLight,
  table,
  source,
  modelName,
  isFile,
  filePreview,
  schemaQuery,
  onSchemaQuery,
  schemaRows,
  columnCount,
  liveStatus,
  liveError,
  onShowAll,
  owner,
  tags,
}: {
  isLight: boolean;
  table: ModelTable;
  source?: DataSource;
  modelName: string;
  isFile: boolean;
  filePreview: FilePreviewResponse | null;
  schemaQuery: string;
  onSchemaQuery: (value: string) => void;
  schemaRows: TableColumn[];
  columnCount: number;
  liveStatus: LiveLoadState;
  liveError: string;
  onShowAll: () => void;
  owner: string;
  tags: string[];
}) {
  const ui = surfaces(isLight);
  const muted = ui.muted;
  const border = ui.border;
  const kindLabel = assetTypeLabel(table, source);
  const connector = source?.connectorKey ? getConnectorDisplayName(source.connectorKey) : source?.connectionType || (source ? SOURCE_META[source.kind]?.label : '');
  const facts: Array<[string, React.ReactNode]> = [
    ['Asset type', kindLabel],
    ['Catalog', modelName || '—'],
    ['Asset id', table.id || '—'],
    ['Connection', source?.name || '—'],
    ['Connector', connector || '—'],
    ['Environment', source?.environment || '—'],
    ['Status', <StatusPill key="status" status={table.status} isLight={isLight} />],
    ['Updated', formatRelative(table.updatedAt) || '—'],
  ];
  if (owner) facts.splice(2, 0, ['Owner', owner]);
  if (isFile) {
    facts.push(
      ['File name', filePreview?.fileName || table.name],
      ['MIME type', filePreview?.mimeType || table.mimeType || '—'],
      [
        'Size',
        filePreview ? formatFileSize(filePreview.size) : '—',
      ],
      ['File id', table.path || '—'],
    );
  } else {
    facts.push(
      ['Path', table.path || table.name],
      ['Columns', liveStatus === 'ready' ? String(columnCount) : '—'],
    );
    if (table.rows > 0) facts.push(['Rows', formatRowCount(table.rows)]);
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <section>
        <h3 className={`mb-1 inline-flex items-center gap-1.5 text-sm font-semibold ${isLight ? 'text-[#1C1C1C]' : 'text-white'}`}>
          Description
          <Pencil size={13} className={muted} />
        </h3>
        <p className={`text-sm leading-6 ${isLight ? 'text-[#3C4048]' : 'text-slate-300'}`}>
          {table.description || 'No description has been added for this asset yet.'}
        </p>
      </section>

      {isFile ? null : (
      <section className={`overflow-hidden rounded-md border ${border}`}>
        <div className={`border-b px-3 py-2.5 ${isLight ? 'bg-[#F7F8FA]' : 'bg-white/[0.03]'} ${border}`}>
          <div className="relative max-w-xs">
            <Search size={14} className={`absolute left-2.5 top-1/2 -translate-y-1/2 ${muted}`} />
            <input value={schemaQuery} onChange={(event) => onSchemaQuery(event.target.value)} placeholder="Filter columns..." className={searchClass(isLight)} />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-[13px]">
            <thead>
              <tr className={`text-left text-[12px] font-medium ${ui.tableHead}`}>
                <th className="px-3 py-2">Column</th>
                <th className="px-3 py-2">Type</th>
                <th className="px-3 py-2">Classification</th>
                <th className="px-3 py-2">Comment</th>
                <th className="px-3 py-2">Tags</th>
              </tr>
            </thead>
            <tbody>
              {liveStatus === 'ready'
                ? schemaRows.slice(0, 8).map((column) => (
                    <tr key={column.name} className={`border-t ${border}`}>
                      <td className={`px-3 py-2 font-mono text-[12.5px] ${isLight ? 'text-[#1C1C1C]' : 'text-white'}`}>{column.name}</td>
                      <td className={`px-3 py-2 ${muted}`}>{column.type}</td>
                      <td className="px-3 py-2">
                        <ClassificationBadge isLight={isLight} value={column.classification} />
                      </td>
                      <td className={`max-w-[280px] truncate px-3 py-2 ${muted}`}>{column.description}</td>
                      <td className="px-3 py-2">
                        {column.tags.map((tag) => (
                          <span key={tag} className={`mr-1 rounded px-1.5 py-0.5 text-[11px] ${tagTone(tag, isLight)}`}>{tag}</span>
                        ))}
                      </td>
                    </tr>
                  ))
                : null}
              {liveStatus !== 'ready' || schemaRows.length === 0 ? (
                <tr>
                  <td colSpan={5} className={`px-3 py-8 text-center text-sm ${muted}`}>
                    {liveNotice(liveStatus, liveError, schemaQuery.trim() ? 'No columns match that filter.' : 'No columns returned.')}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
        {liveStatus === 'ready' && schemaRows.length > 8 ? (
          <button type="button" onClick={onShowAll} className={`border-t px-3 py-2 text-[13px] font-medium ${border} ${isLight ? 'text-[#1B6CA8]' : 'text-sky-300'}`}>
            View all {columnCount} columns
          </button>
        ) : null}
      </section>
      )}

      <section className={`border-t pt-4 ${border}`}>
        <h3 className={`mb-3 text-sm font-semibold ${isLight ? 'text-[#1C1C1C]' : 'text-white'}`}>About this {kindLabel.toLowerCase()}</h3>
        <dl className="grid max-w-xl grid-cols-[140px_minmax(0,1fr)] gap-y-2.5 text-sm">
          {facts.map(([label, value]) => (
            <React.Fragment key={label}>
              <dt className={muted}>{label}</dt>
              <dd className={isLight ? 'text-[#1C1C1C]' : 'text-white'}>
                {label === 'Connection' && source ? (
                  <span className={`inline-flex items-center gap-1.5 ${isLight ? 'text-[#1B6CA8]' : 'text-sky-300'}`}>
                    <SourceIconTile kind={source.kind} isLight={isLight} box="h-5 w-5" />
                    {value}
                  </span>
                ) : (
                  value
                )}
              </dd>
            </React.Fragment>
          ))}
        </dl>
      </section>

      {tags.length ? (
        <section className={`border-t pt-4 ${border}`}>
          <h3 className={`mb-2 text-sm font-semibold ${isLight ? 'text-[#1C1C1C]' : 'text-white'}`}>Tags</h3>
          <div className="flex flex-wrap gap-1.5">
            {tags.map((tag) => (
              <span key={tag} className={`rounded px-1.5 py-0.5 text-xs ${tagTone(tag, isLight)}`}>{tag}</span>
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}

function SchemaCard({
  isLight,
  total,
  rows,
  query,
  onQuery,
  onClassify,
  onSave,
  dirty,
  liveStatus,
  liveError,
  onShowAll,
  showAll,
}: {
  isLight: boolean;
  total: number;
  rows: TableColumn[];
  query: string;
  onQuery: (value: string) => void;
  onClassify: (columnName: string, value: Classification | null) => void;
  onSave: () => void;
  dirty: boolean;
  liveStatus: LiveLoadState;
  liveError: string;
  onShowAll?: () => void;
  showAll?: boolean;
}) {
  const ui = surfaces(isLight);
  const muted = ui.muted;
  const border = ui.border;
  const card = ui.card;
  return (
    <article className={`relative rounded-2xl border p-4 ${card}`}>
      <button
        type="button"
        disabled={!dirty}
        onClick={onSave}
        className={`${ghostBtn(isLight)} absolute right-4 top-4 !h-7 !px-2 text-[12px]`}
      >
        <Save size={12} />
        Save
      </button>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2 pr-20">
        <h3 className={`inline-flex items-center gap-2 text-sm font-semibold ${isLight ? 'text-neutral-900' : 'text-white'}`}>
          <Columns3 size={15} />
          Schema ({total} columns)
        </h3>
        <div className="flex items-center gap-2">
          <div className="relative w-48">
            <Search size={14} className={`absolute left-2.5 top-1/2 -translate-y-1/2 ${muted}`} />
            <input value={query} onChange={(event) => onQuery(event.target.value)} placeholder="Search columns..." className={searchClass(isLight)} />
          </div>
        </div>
      </div>
      <div className={`overflow-x-auto rounded-xl border ${border}`}>
        <table className="min-w-full font-sans text-[13px] leading-5">
          <thead className={ui.tableHead}>
            <tr className="text-left text-[11px] font-semibold uppercase tracking-wider">
              <th className="px-3 py-2">#</th>
              <th className="px-3 py-2">Column Name</th>
              <th className="px-3 py-2">Data Type</th>
              <th className="px-3 py-2">Classification</th>
              <th className="px-3 py-2">Nullable</th>
              <th className="px-3 py-2">Key</th>
              <th className="px-3 py-2">Description</th>
              <th className="px-3 py-2">Tags</th>
            </tr>
          </thead>
          <tbody>
            {liveStatus === 'ready'
              ? rows.map((column, index) => (
                  <tr key={column.name} className={`border-t ${border}`}>
                    <td className={`px-3 py-2 ${muted}`}>{index + 1}</td>
                    <td className={`px-3 py-2 font-mono text-[12.5px] font-medium ${isLight ? 'text-neutral-900' : 'text-white'}`}>{column.name}</td>
                    <td className="px-3 py-2">{column.type}</td>
                    <td className="px-3 py-2">
                      <ClassificationSelect isLight={isLight} column={column} onChange={(value) => onClassify(column.name, value)} />
                    </td>
                    <td className="px-3 py-2">{column.nullable ? 'Yes' : 'No'}</td>
                    <td className="px-3 py-2">{column.key || '—'}</td>
                    <td className={`max-w-[220px] truncate px-3 py-2 ${muted}`}>{column.description}</td>
                    <td className="px-3 py-2">
                      {column.tags.map((tag) => (
                        <span key={tag} className={`mr-1 rounded-full px-1.5 py-0.5 text-[11px] ${tagTone(tag, isLight)}`}>
                          {tag}
                        </span>
                      ))}
                    </td>
                  </tr>
                ))
              : null}
            {liveStatus !== 'ready' || rows.length === 0 ? (
              <tr>
                <td colSpan={8} className={`px-3 py-8 text-center text-sm ${muted}`}>
                  {liveNotice(liveStatus, liveError, query.trim() ? 'No columns match that filter.' : 'No columns returned.')}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
      {!showAll && onShowAll ? (
        <button type="button" onClick={onShowAll} className={`mt-3 inline-flex items-center gap-1 text-sm ${isLight ? 'text-[#1B6CA8]' : 'text-sky-300'}`}>
          View all {total} columns
          <ArrowRight size={14} />
        </button>
      ) : null}
    </article>
  );
}

function PreviewCard({
  isLight,
  preview,
  liveStatus,
  liveError,
  paging,
  page,
  pageSize,
  onPage,
  onPageSize,
  searchApplied,
  onSearch,
  onQuery,
  onMore,
  full,
}: {
  isLight: boolean;
  preview: TablePreviewResponse | null;
  liveStatus: LiveLoadState;
  liveError: string;
  paging?: boolean;
  page: number;
  pageSize: number;
  onPage: (page: number) => void;
  onPageSize: (pageSize: number) => void;
  searchApplied?: string;
  onSearch?: (term: string) => void;
  onQuery?: () => void;
  onMore?: () => void;
  full?: boolean;
}) {
  const ui = surfaces(isLight);
  const muted = ui.muted;
  const border = ui.border;
  const card = ui.card;
  const [searchDraft, setSearchDraft] = useState(searchApplied || '');
  const [expanded, setExpanded] = useState(false);
  useEffect(() => {
    setSearchDraft(searchApplied || '');
  }, [searchApplied]);
  useEffect(() => {
    if (!expanded) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setExpanded(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [expanded]);
  const columnHeaders = preview?.columns.map((column) => column.name).filter(Boolean) ?? [];
  const headers = columnHeaders.length ? columnHeaders : preview?.rows[0] ? Object.keys(preview.rows[0]) : [];
  const notice = liveNotice(liveStatus, liveError, searchApplied ? 'No rows match that search.' : 'No rows returned.');
  const total = preview?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize) || 1);
  const current = Math.min(Math.max(1, preview?.page || page), totalPages);
  const from = total === 0 ? 0 : (current - 1) * pageSize + 1;
  const to = Math.min(current * pageSize, total);
  const applySearch = (term: string) => onSearch?.(term.trim().slice(0, 120));
  const searchFrame = isLight
    ? 'border-[#D8DCE2] bg-white focus-within:border-[#1B6CA8] focus-within:ring-2 focus-within:ring-[#1B6CA8]/15'
    : 'border-white/15 bg-black focus-within:border-white/30 focus-within:ring-2 focus-within:ring-white/10';
  return (
    <>
      {expanded ? <div className="fixed inset-0 z-40 bg-black/40" onClick={() => setExpanded(false)} /> : null}
      <article className={previewShellClass(isLight, expanded, card)}>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className={`inline-flex items-center gap-2 text-sm font-semibold ${isLight ? 'text-neutral-900' : 'text-white'}`}>
          <LayoutDashboard size={15} />
          Data Preview
          {paging ? <span className={`text-xs font-normal ${muted}`}>Loading page…</span> : null}
        </h3>
        <div className="flex flex-wrap items-center gap-2">
          {onSearch ? (
            <form
              className={`flex h-8 overflow-hidden rounded-md border ${searchFrame}`}
              onSubmit={(event) => {
                event.preventDefault();
                applySearch(searchDraft);
              }}
            >
              <input
                value={searchDraft}
                onChange={(event) => setSearchDraft(event.target.value)}
                placeholder="Search rows..."
                maxLength={120}
                disabled={liveStatus === 'loading' || liveStatus === 'missing'}
                className={`h-full min-w-[13rem] flex-1 bg-transparent px-3 text-sm outline-none ${
                  isLight ? 'text-[#1C1C1C] placeholder:text-[#9AA1AB]' : 'text-slate-100 placeholder:text-slate-500'
                }`}
              />
              {searchDraft || searchApplied ? (
                <button
                  type="button"
                  className={`inline-flex h-8 w-8 items-center justify-center ${
                    isLight ? 'text-[#6B7280] hover:text-[#1C1C1C]' : 'text-slate-400 hover:text-white'
                  }`}
                  aria-label="Clear search"
                  title="Clear search"
                  onClick={() => {
                    setSearchDraft('');
                    if (searchApplied) applySearch('');
                  }}
                >
                  <X size={13} />
                </button>
              ) : null}
              <button
                type="submit"
                className={`inline-flex h-8 w-8 items-center justify-center border-l ${border} ${
                  isLight ? 'text-[#4B5563] hover:bg-[#F7F8FA]' : 'text-slate-200 hover:bg-white/5'
                }`}
                aria-label="Search"
                title="Search"
                disabled={paging || liveStatus === 'loading' || liveStatus === 'missing'}
              >
                <Search size={14} />
              </button>
            </form>
          ) : null}
          <button type="button" className={ghostBtn(isLight)} onClick={onQuery}>
            <Play size={13} />
            Query Data
          </button>
          <PreviewExpandButton isLight={isLight} expanded={expanded} onToggle={() => setExpanded((value) => !value)} />
        </div>
      </div>
      <div className={`${expanded ? 'min-h-0 flex-1' : ''} overflow-x-auto rounded-xl border ${border} ${paging ? 'opacity-70' : ''}`}>
        {liveStatus !== 'ready' || headers.length === 0 ? (
          <p className={`px-3 py-8 text-center text-sm ${muted}`}>{notice}</p>
        ) : (
          <table className="min-w-full font-mono text-[12.5px]">
            <thead className={ui.tableHead}>
              <tr className="text-left text-[11px] font-semibold uppercase tracking-wider">
                {headers.map((header) => (
                  <th key={header} className="px-3 py-2">
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(preview?.rows ?? []).map((row, index) => (
                <tr key={index} className={`border-t ${border}`}>
                  {headers.map((header) => (
                    <td key={header} className={`whitespace-nowrap px-3 py-2 ${isLight ? 'text-neutral-800' : 'text-neutral-200'}`}>
                      {formatPreviewCell(row[header])}
                    </td>
                  ))}
                </tr>
              ))}
              {(preview?.rows.length ?? 0) === 0 ? (
                <tr>
                  <td colSpan={headers.length} className={`px-3 py-8 text-center text-sm ${muted}`}>
                    {searchApplied ? 'No rows match that search.' : 'No rows returned.'}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        )}
      </div>
      {liveStatus === 'ready' ? (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
          <p className={`text-xs ${muted}`}>
            {total === 0 ? '0 rows' : `${from}–${to} of ${total} rows`}
            {searchApplied ? ` matching “${searchApplied}”` : ''}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <label className={`inline-flex items-center gap-1.5 text-xs ${muted}`}>
              Rows
              <select
                value={pageSize}
                disabled={paging}
                onChange={(event) => onPageSize(Number(event.target.value))}
                className={`${searchClass(isLight)} !h-8 !w-auto !pl-2 !pr-6`}
              >
                {PREVIEW_PAGE_SIZES.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </label>
            <button type="button" className={ghostBtn(isLight)} disabled={paging || current <= 1} onClick={() => onPage(current - 1)}>
              <ChevronLeft size={14} />
            </button>
            <span className={`text-xs tabular-nums ${muted}`}>
              {current} / {totalPages}
            </span>
            <button
              type="button"
              className={ghostBtn(isLight)}
              disabled={paging || current >= totalPages}
              onClick={() => onPage(current + 1)}
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      ) : null}
      {!full && onMore ? (
        <button type="button" onClick={onMore} className={`mt-3 inline-flex items-center gap-1 text-sm ${isLight ? 'text-[#1B6CA8]' : 'text-sky-300'}`}>
          View more rows
          <ArrowRight size={14} />
        </button>
      ) : null}
    </article>
    </>
  );
}

function RelationshipsCard({ isLight, relations }: { isLight: boolean; relations: ReturnType<typeof buildTableDetail>['relationships'] }) {
  const ui = surfaces(isLight);
  const muted = ui.muted;
  const card = ui.card;
  return (
    <article className={`rounded-2xl border p-4 ${card}`}>
      <div className="mb-4 flex items-center justify-between">
        <h3 className={`inline-flex items-center gap-2 text-sm font-semibold ${isLight ? 'text-neutral-900' : 'text-white'}`}>
          <GitBranch size={15} />
          Relationships ({relations.length})
        </h3>
        <span className={`text-sm ${isLight ? 'text-[#1B6CA8]' : 'text-sky-300'}`}>View all relationships</span>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {relations.map((relation, index) => (
          <React.Fragment key={relation.name}>
            <div className={`min-w-[120px] rounded-md border px-3 py-2 ${isLight ? 'border-[#E6E8EB] bg-white' : 'border-white/10 bg-white/5'}`}>
              <p className={`font-mono text-xs font-medium ${isLight ? 'text-neutral-900' : 'text-white'}`}>{relation.name}</p>
              <p className={`text-[11px] ${muted}`}>{relation.kind}</p>
              <p className={`text-[11px] ${muted}`}>{relation.field}</p>
            </div>
            {index < relations.length - 1 ? <ArrowRight size={14} className={muted} /> : null}
          </React.Fragment>
        ))}
      </div>
    </article>
  );
}


function UsageCard({ isLight, usage }: { isLight: boolean; usage: Array<{ label: string; value: string }> }) {
  const ui = surfaces(isLight);
  const muted = ui.muted;
  const card = ui.card;
  return (
    <div className="space-y-3">
      <h3 className={`inline-flex items-center gap-2 text-sm font-semibold ${isLight ? 'text-neutral-900' : 'text-white'}`}>
        <Activity size={15} />
        Usage
      </h3>
      <div className="grid gap-3 sm:grid-cols-2">
        {usage.map((item) => (
          <div key={item.label} className={`rounded-2xl border px-4 py-3 ${card}`}>
            <p className={`text-lg font-semibold ${isLight ? 'text-neutral-900' : 'text-white'}`}>{item.value}</p>
            <p className={`text-xs ${muted}`}>{item.label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function LineageCard({
  isLight,
  table,
  source,
  relations,
}: {
  isLight: boolean;
  table: ModelTable;
  source?: DataSource;
  relations: ReturnType<typeof buildTableDetail>['relationships'];
}) {
  const ui = surfaces(isLight);
  const muted = ui.muted;
  const card = ui.card;
  return (
    <article className={`rounded-2xl border p-4 ${card}`}>
      <h3 className={`mb-3 inline-flex items-center gap-2 text-sm font-semibold ${isLight ? 'text-neutral-900' : 'text-white'}`}>
        <GitBranch size={15} />
        Lineage
      </h3>
      <p className={`text-sm ${muted}`}>
        {source ? SOURCE_META[source.kind].label : 'Source'} → {table.name}
        {relations.length ? ` → ${relations.map((relation) => relation.name).join(' → ')}` : ''}
      </p>
    </article>
  );
}

function ClassificationSelect({
  isLight,
  column,
  onChange,
}: {
  isLight: boolean;
  column: TableColumn;
  onChange: (value: Classification | null) => void;
}) {
  const current = CLASSIFICATIONS.includes(column.classification as Classification)
    ? (column.classification as Classification)
    : '';
  return (
    <select
      aria-label={`Classification for ${column.name}`}
      value={current}
      onChange={(event) => onChange((event.target.value || null) as Classification | null)}
      className={
        isLight
          ? 'h-7 min-w-[7.5rem] rounded-md border border-[#D8DCE2] bg-white px-2 text-[12px] text-[#1C1C1C] focus:border-[#1B6CA8] focus:outline-none'
          : 'h-7 min-w-[7.5rem] rounded-md border border-white/15 bg-black px-2 text-[12px] text-slate-100 focus:border-white/30 focus:outline-none'
      }
    >
      <option value="">Unclassified</option>
      {CLASSIFICATIONS.map((item) => (
        <option key={item} value={item}>{item}</option>
      ))}
    </select>
  );
}

function ClassificationBadge({ isLight, value }: { isLight: boolean; value?: string }) {
  if (!value) return <span className={isLight ? 'text-[#9AA1AB]' : 'text-slate-500'}>—</span>;
  return <span className={`rounded px-1.5 py-0.5 text-[11px] ${tagTone(value, isLight)}`}>{value}</span>;
}
